import { getNormalizedPathFromSlugOrPathNames } from '@unifyapps/carbon/no-code/utils/path/interfacePathNormalize';
import type { InterfaceEntity, NavItem } from '@unifyapps/defs/types/interface';
import omit from 'lodash/omit';
import { InterfaceModes } from '../../../no-code/stores/InterfaceStore/types';
import {
  getInterfacePageBuilderRoute,
  getInterfacePreviewRoute,
} from '../../../no-code/utils/interfaceRoutes';
import {
  hasUnresolvedDynamicPathParams,
  resolveDynamicPath,
} from '../../../no-code/utils/path/interfacePathDynamicParamsHandlers';
import { serializePageInputsToSearchParams } from '../../../no-code/utils/serializePageInputsToSearchParams';
import type { NavItem as SideNavItem } from '../types';
import { getInterfacePagePathFromUrl } from './getInterfacePagePathFromUrl';

const getUrlFromPageSlug = ({
  interfaceId,
  interfacePagePathOrSlug,
  mode,
  pageInputs,
}: {
  interfaceId: string;
  interfacePagePathOrSlug: string;
  mode: InterfaceModes;
  pageInputs?: Record<string, unknown>;
}) => {
  switch (mode) {
    case InterfaceModes.BUILDER:
      return getInterfacePageBuilderRoute({
        interfaceId,
        interfacePagePathOrSlug,
        pageInputs,
      });
    case InterfaceModes.PREVIEW:
    case InterfaceModes.RUNNER:
      return getInterfacePreviewRoute({
        interfaceId,
        interfacePagePathOrSlug,
        pageInputs,
      });
  }
};

const getRunnerUrl = (path: string, pageInputs?: Record<string, unknown>) => {
  const queryString = serializePageInputsToSearchParams(pageInputs).toString();
  return queryString ? `/${path}?${queryString}` : `/${path}`;
};

export function getNavItemWithAdaptedUrl({
  navItem,
  interfaceRecord,
  mode,
}: {
  navItem: Extract<NavItem, { type: 'URL' } | { type: 'PAGE' } | { type: 'MODULE' }>;
  interfaceRecord: InterfaceEntity;
  mode: InterfaceModes;
}): SideNavItem {
  switch (navItem.type) {
    case 'URL': {
      const internalPage = getInterfacePagePathFromUrl(navItem.url);

      if (!internalPage) {
        return navItem;
      }
      // internal path targets an interface page — resolve it through the same mode resolver PAGE
      // items use so the href AND active-state match the real builder/preview/runner route.
      const { path, suffix } = internalPage;
      return {
        ...navItem,
        url:
          mode === InterfaceModes.RUNNER
            ? `${getRunnerUrl(path)}${suffix}`
            : `${getUrlFromPageSlug({
                interfaceId: interfaceRecord.id,
                interfacePagePathOrSlug: path,
                mode,
              })}${suffix}`,
      };
    }
    case 'MODULE': {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- slug is not present in old interfaces
      const slug = navItem.slug ?? navItem.moduleId;
      return {
        ...navItem,
        type: 'URL',
        url:
          mode === InterfaceModes.RUNNER
            ? getRunnerUrl(slug, navItem.pageInputs)
            : getUrlFromPageSlug({
                interfaceId: interfaceRecord.id,
                interfacePagePathOrSlug: slug,
                mode,
                pageInputs: navItem.pageInputs,
              }),
      };
    }
    case 'PAGE': {
      // type can be PAGE with no page picked (e.g. BRAND_NAVIGATION switched from URL), leaving
      // path/slug/pageId all undefined — fall back to '' so resolveDynamicPath gets a string.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- slug is not present in old interfaces
      const path = getNormalizedPathFromSlugOrPathNames(
        resolveDynamicPath(
          navItem.path || navItem.slug || navItem.pageId || '',
          navItem.pageParams,
        ),
      );
      return {
        ...omit(navItem, 'pageId'),
        type: 'URL',
        isInvalidUrl: hasUnresolvedDynamicPathParams(path),
        url:
          mode === InterfaceModes.RUNNER
            ? getRunnerUrl(path, navItem.pageInputs)
            : getUrlFromPageSlug({
                interfaceId: interfaceRecord.id,
                interfacePagePathOrSlug: path,
                mode,
                pageInputs: navItem.pageInputs,
              }),
      };
    }
  }
}
